import { SphereTooltip } from "@components/common/sphere-tooltip";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { GROUPS_TABLE_COLUMN_LABELS } from "@components/table/groups/groups-table-constants";
import {
  GroupsTableColumnField,
  GroupsTableGridColDef,
} from "@components/table/groups/groups-table-types";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  THUMBNAIL_IMAGE_HEIGHT,
  THUMBNAIL_IMAGE_WIDTH,
  withEllipsis,
} from "@styles/common-styles";
// Here we do not need it as a React component because will be used as src for an image
import defaultGroupThumbnail from "@assets/thumbnails/groups-thumbs/group-thumbnail_default.svg";
import { GroupTypes } from "@custom-types/group-types";
import {
  isGroupDetails,
  isGroupsTableColumnField,
} from "@custom-types/type-guards";
import { useDateTime } from "@hooks/use-date-time";
import { getProjectNumbersInGroup } from "@utils/group-utils";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { Box, Stack } from "@mui/material";
import { useMediaQueryList } from "@hooks/use-media-query";

/** Minimum column width in px */
const COLS_MIN_WIDTH_IN_PX = 100;

/** Column width in px for medium or smaller screen */
const MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX = 200;

/** Column width in px for large and larger screens */
const LG_OR_LARGER_COLUMNS_WIDTH_IN_PX = 250;

/** Return all the possible columns for Groups table */
export function useGroupsColumns(): GroupsTableGridColDef[] {
  const { isScreenMd, isScreenLgOrLarger } = useMediaQueryList();
  const { formatDate } = useDateTime();

  const maxTextColumnWidth = isScreenLgOrLarger
    ? LG_OR_LARGER_COLUMNS_WIDTH_IN_PX
    : MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX;

  const flexSetting = isScreenLgOrLarger ? 1 : undefined;

  const { columnPreferences, visibleOrderedColumns } =
    useColumnPreferencesContext<GroupsTableColumnField>();

  const columnsMap: Record<GroupsTableColumnField, GroupsTableGridColDef> = {
    name: {
      field: "name",
      headerName: GROUPS_TABLE_COLUMN_LABELS.name,
      flex: 1,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      disableReorder: true,
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            gap="0.625rem"
            sx={{ ...withEllipsis }}
          >
            <Box
              component="img"
              // Don't use Nullish coalescing (??) as it might happen that the thumbnailUrl is an empty string
              src={
                params.row.entity.thumbnailUrl
                  ? params.row.entity.thumbnailUrl
                  : defaultGroupThumbnail
              }
              sx={{
                height: `${THUMBNAIL_IMAGE_HEIGHT}px`,
                width: `${THUMBNAIL_IMAGE_WIDTH}px`,
                objectFit: "cover",
              }}
              alt="Group thumbnail"
            />
            <SphereTooltip
              title={<var>{params.row.entity.name}</var>}
              boxProps={{
                sx: {
                  ...withEllipsis,
                  fontWeight: "bold",
                },
              }}
            >
              <var>{params.row.entity.name}</var>
            </SphereTooltip>
          </Stack>
        );
      },
    },

    members: {
      field: "members",
      width: isScreenMd
        ? MD_OR_SMALLER_COLUMNS_WIDTH_IN_PX
        : LG_OR_LARGER_COLUMNS_WIDTH_IN_PX,
      minWidth: 54,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={GROUPS_TABLE_COLUMN_LABELS.members}
        />
      ),
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        if (!isGroupDetails(params.row.entity)) {
          return <var>{params.row.entity.numManagers}</var>;
        }
      },
    },

    projects: {
      field: "projects",
      flex: flexSetting,
      width: maxTextColumnWidth,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={GROUPS_TABLE_COLUMN_LABELS.projects}
        />
      ),
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        return <var>{getProjectNumbersInGroup(params.row.entity)}</var>;
      },
    },

    createdDate: {
      field: "createdDate",
      flex: flexSetting,
      minWidth: COLS_MIN_WIDTH_IN_PX,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
      sortable: true,
      renderHeader: () => (
        <TableHeaderWithDraggableIcon
          title={GROUPS_TABLE_COLUMN_LABELS.createdDate}
        />
      ),
      renderCell: (params: GridRenderCellParams<{ entity: GroupTypes }>) => {
        return <var>{formatDate(params.row.entity.createdAt)}</var>;
      },
    },

    // A placeholder for further options
    options: {
      field: "options",
      type: "actions",
      align: "right",
      headerName: "",
      width: 90,
      renderHeader: () => (
        <FaroTableColumnVisibilityMenuIcon
          columnPreferences={columnPreferences}
        />
      ),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      disableReorder: true,
      renderCell: () => {
        return "";
      },
    },
  };

  // Return only visible columns in the order specified by preferences
  return visibleOrderedColumns
    .filter(isGroupsTableColumnField)
    .map((field) => columnsMap[field]);
}
